class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  /**
   * SPEED (add to cart): opens the drawer at once with the line being added, built from data the
   * product page already has, while /cart/add is still on its way. The subtotal shows a loading
   * state and Checkout waits. renderContents() then replaces everything with the server's drawer.
   * Returns a function that undoes it all (used when the add fails), or null when it cannot run.
   * line: { variantId, quantity, url, image: {src, alt, h}, options: [[name, value]], unitPrice, linePrice }
   */
  showPending(line) {
    const template = document.getElementById('CartDrawer-PendingTemplate');
    const items = this.querySelector('#CartDrawer-CartItems');
    if (!template || !items || !line) return null;

    const undo = [];
    const wasOpen = this.classList.contains('active');
    const setClass = (element, name, on) => {
      if (!element || element.classList.contains(name) === on) return;
      element.classList.toggle(name, on);
      undo.push(() => element.classList.toggle(name, !on));
    };

    const existing = this.querySelector(`.cart-item .quantity__input[data-quantity-variant-id="${line.variantId}"]`);
    if (existing) {
      // Same variant already in the cart: the line just grows, as it will on the server.
      const before = existing.value;
      existing.value = Number(before) + line.quantity;
      undo.push(() => (existing.value = before));
      const row = existing.closest('.cart-item');
      const spinner = row?.querySelector('.cart-item__totals .loading__spinner');
      setClass(spinner, 'hidden', false);
    } else {
      const fragment = template.content.cloneNode(true);
      const row = fragment.querySelector('tr[data-cart-pending]');
      row.querySelectorAll('[data-slot="link"]').forEach((link) => (link.href = line.url));
      const image = row.querySelector('[data-slot="image"]');
      if (line.image) {
        image.src = line.image.src;
        image.alt = line.image.alt || '';
        image.height = line.image.h || 150;
      } else {
        image.remove();
      }
      row.querySelector('[data-slot="unit"]').textContent = line.unitPrice;
      row.querySelector('[data-slot="line"]').textContent = line.linePrice;
      row.querySelector('[data-slot="quantity"]').value = line.quantity;
      const options = row.querySelector('[data-slot="options"]');
      if (options) {
        line.options.forEach(([name, value]) => {
          const option = document.createElement('div');
          option.className = 'product-option';
          const dt = document.createElement('dt');
          dt.textContent = `${name}:`;
          const dd = document.createElement('dd');
          dd.textContent = value;
          option.append(dt, dd);
          options.append(option);
        });
      }

      const tbody = items.querySelector('.cart-items tbody');
      if (tbody) {
        tbody.prepend(row);
        undo.push(() => row.remove());
      } else {
        const wrapper = fragment.querySelector('[data-cart-pending]');
        items.prepend(wrapper);
        undo.push(() => wrapper.remove());
      }
    }

    // Empty drawer -> filled drawer, the same classes renderContents() and product-form.js drop.
    setClass(this, 'is-empty', false);
    setClass(this.querySelector('.drawer__inner'), 'is-empty', false);
    setClass(this.querySelector('cart-drawer-items'), 'is-empty', false);
    // The empty-cart block is still in the flow (only its message hides): take it out, or it pushes
    // the header and the line down and squeezes the line to nothing.
    const emptyBlock = this.querySelector('.drawer__inner-empty');
    if (emptyBlock && emptyBlock.style.display !== 'none') {
      emptyBlock.style.display = 'none';
      undo.push(() => emptyBlock.style.removeProperty('display'));
    }

    const heading = this.querySelector('.drawer__heading');
    if (heading) {
      const before = heading.textContent;
      heading.textContent = before.replace(/\((\d+)\)/, (match, count) => `(${Number(count) + line.quantity})`);
      undo.push(() => (heading.textContent = before));
    }

    const total = this.querySelector('.totals__total-value');
    setClass(total, 'is-loading', true);
    if (total) {
      total.setAttribute('aria-busy', 'true');
      undo.push(() => total.removeAttribute('aria-busy'));
    }

    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout && !checkout.disabled) {
      checkout.disabled = true;
      undo.push(() => (checkout.disabled = false));
    }

    this.open();

    return () => {
      undo.reverse().forEach((step) => step());
      if (!wasOpen) this.close();
    };
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
